import type { ReactNode } from 'react'
import type { AgencyAdminRow } from '@/features/agencies/models/agency-admin'
import type { AgencyV2AdminRow } from '@/features/agencies/models/agency-admin-v2'
import type { IClientAdminRow } from '@/features/clients/models/client-admin'
import type { ClientsAdminV2Row } from '@/features/clients/services/clients-admin-v2.service'
import type { CompanyAdminRow } from '@/features/companies/models/company-admin'
import type { CompanyV2AdminRow } from '@/features/companies/models/company-admin-v2'
import type { CreditListRow } from '@/features/credits/models/credit-list'
import type { HandoverRow } from '@/features/deliveries/models/delivery-v2'
import type { IInventoryDetail } from '@/features/inventory/models/inventory'
import type { ProfessionAdminRow } from '@/features/professions/models/profession-admin'
import type { ProfessionV2AdminRow } from '@/features/professions/models/profession-admin-v2'
import type { ProjectAdminRow } from '@/features/projects/models/project-admin'
import type { ProjectV2AdminRow } from '@/features/projects/models/project-admin-v2'
import type { PropertyAdminRow } from '@/features/properties/models/property-admin'
import type { PropertyV2AdminRow } from '@/features/properties/models/property-admin-v2'
import type { RepresentativeAdminRow } from '@/features/representatives/models/representative-admin'
import type { RepresentativeV2AdminRow } from '@/features/representatives/models/representative-admin-v2'
import type { RoleAdminRow } from '@/features/roles/model/role-admin'
import type { RoleV2AdminRow } from '@/features/roles/model/role-admin-v2'
import type { SocietyAdminRow } from '@/features/societies/models/society-admin'
import type { SocietyV2AdminRow } from '@/features/societies/models/society-admin-v2'
import type { StateAdminRow } from '@/features/states/models/state-admin'
import type { StateV2AdminRow } from '@/features/states/models/state-admin-v2'
import type { TypePropertyAdminRow } from '@/features/type-properties/models/type-property-admin'
import type { TypePropertyV2AdminRow } from '@/features/type-properties/models/type-property-admin-v2'
import type { UserAdminRow } from '@/features/users/models/user-admin'
import type { UserV2AdminRow } from '@/features/users/models/user-admin-v2'
import type { WorkflowAdminRow } from '@/features/workflows/models/workflow-admin'
import type { WorkflowV2AdminRow } from '@/features/workflows/models/workflow-admin-v2'

export const SIDE_PANEL_WIDTH = {
  min: 320,
  default: 400,
  max: 600
} as const

export const SIDE_PANEL_STORAGE_KEY = 'idc:side-panel-width'

export type SidePanelPage =
  | 'inventory-detail'
  | 'client-detail'
  | 'client-detail-v2'
  | 'client-account-v2-detail'
  | 'client-create'
  | 'portal-flow-detail'
  | 'portal-flow-create'
  | 'negotiation-detail'
  | 'credit-detail'
  | 'credit-audit'
  | 'delivery-detail'
  | 'property-detail'
  | 'property-create'
  | 'property-v2-detail'
  | 'property-v2-create'
  | 'user-detail'
  | 'user-create'
  | 'user-v2-detail'
  | 'user-v2-create'
  | 'profession-detail'
  | 'profession-create'
  | 'profession-v2-detail'
  | 'profession-v2-create'
  | 'type-property-detail'
  | 'type-property-create'
  | 'type-property-v2-detail'
  | 'type-property-v2-create'
  | 'project-detail'
  | 'project-create'
  | 'project-v2-detail'
  | 'project-v2-create'
  | 'society-detail'
  | 'society-create'
  | 'society-v2-detail'
  | 'society-v2-create'
  | 'representative-detail'
  | 'representative-create'
  | 'representative-v2-detail'
  | 'representative-v2-create'
  | 'company-detail'
  | 'company-create'
  | 'company-v2-detail'
  | 'company-v2-create'
  | 'agency-detail'
  | 'agency-create'
  | 'agency-v2-detail'
  | 'agency-v2-create'
  | 'state-detail'
  | 'state-v2-detail'
  | 'workflow-detail'
  | 'workflow-create'
  | 'workflow-v2-detail'
  | 'workflow-v2-create'
  | 'role-detail'
  | 'role-v2-detail'

export interface SidePanelPageParams {
  'inventory-detail': {
    propertyId: string
    propertyCode?: string
    onReserve?: (item: IInventoryDetail) => void
    onRelease?: (item: IInventoryDetail) => void
  }
  'client-detail': {
    client: IClientAdminRow
  }
  'client-detail-v2': {
    client: ClientsAdminV2Row
  }
  'client-create': {
    onDone?: () => void
  }
  'portal-flow-detail': {
    flowId: string
    clientId: string
    clientName: string
    clientType: 'individual' | 'juridical'
  }
  'portal-flow-create': {
    clientId: string
    clientType: 'individual' | 'juridical'
    clientName: string
    clientEmail: string | null
  }
  'negotiation-detail': {
    negotiationId: string
    negotiationCode?: number
  }
  /**
   * El trámite de crédito de una negociación cerrada. Recibe la FILA porque el expediente es
   * OPCIONAL: sin él el panel ofrece abrirlo, con él muestra su ficha. Ver `credit-detail-panel`.
   */
  'credit-detail': {
    row: CreditListRow
  }
  /**
   * La bitácora de UN expediente. Recibe el `crd_` y no el detalle entero: la pide por su cuenta
   * (`useAuditLogQuery`), así el disparador la abre desde cualquier lado sin cargar antes nada.
   *
   * `creditCode` es sólo para el título — el correlativo que se lee en pantalla.
   */
  'credit-audit': {
    creditId: string
    creditCode?: number
  }
  /**
   * El resumen de una fila de Postventa: negociación · crédito · cliente. Recibe la FILA, que
   * existe tenga o no acta, y ya trae todo lo que el panel muestra. Ver `delivery-detail-panel`.
   */
  'delivery-detail': {
    row: HandoverRow
  }
  // Catálogo de inmuebles (CRUD). La disponibilidad vive en 'inventory-detail'.
  'property-detail': {
    property: PropertyAdminRow
  }
  'property-create': {
    onDone?: () => void
  }
  'property-v2-detail': {
    property: PropertyV2AdminRow
  }
  'property-v2-create': {
    onDone?: () => void
  }
  // Usuarios de plataforma (CRUD). Credenciales fuera: son self-service.
  'user-detail': {
    user: UserAdminRow
  }
  'user-create': {
    onDone?: () => void
  }
  // Usuarios de plataforma vía API v2 (id público `usr_`, detrás de webAuthV2).
  'user-v2-detail': {
    user: UserV2AdminRow
  }
  'user-v2-create': {
    onDone?: () => void
  }
  // Catálogo de profesiones (CRUD). La fila "Otro" es de solo lectura.
  'profession-detail': {
    profession: ProfessionAdminRow
  }
  // Profesiones vía API v2 (id público `prf_`, detrás de webAuthV2).
  'profession-v2-detail': {
    profession: ProfessionV2AdminRow
  }
  'profession-v2-create': {
    onDone?: () => void
  }
  'profession-create': {
    onDone?: () => void
  }
  // Catálogo de subtipos de inmueble (CRUD, código único por proyecto).
  'type-property-detail': {
    typeProperty: TypePropertyAdminRow
  }
  'type-property-create': {
    onDone?: () => void
  }
  // Subtipos vía API v2 (id público `typ_`, proyecto por `prj_`, autoría por
  // `usr_`, detrás de webAuthV2).
  'type-property-v2-detail': {
    typeProperty: TypePropertyV2AdminRow
  }
  'type-property-v2-create': {
    onDone?: () => void
  }
  // Proyectos inmobiliarios (CRUD; plantillas de documentos en página propia).
  'project-detail': {
    project: ProjectAdminRow
  }
  'project-create': {
    onDone?: () => void
  }
  // Proyectos vía API v2 (id público `prj_`, sociedad por `soc_`, autorizadores
  // por `usr_`, detrás de webAuthV2).
  'client-account-v2-detail': {
    /**
     * El `cac_`, no la fila del listado. El panel carga su propio detalle, y eso es lo
     * que permite que un enlace `?cuenta=cac_…` abra la cuenta aunque no esté en la
     * página actual de la tabla, y que la pestaña «App» de la ficha del cliente reuse el
     * mismo panel sin duplicarlo.
     */
    accountId: string
  }
  'project-v2-detail': {
    project: ProjectV2AdminRow
  }
  'project-v2-create': {
    onDone?: () => void
  }
  // Sociedades legales (CRUD; credenciales SAP encriptadas, nunca visibles).
  'society-detail': {
    society: SocietyAdminRow
  }
  'society-create': {
    onDone?: () => void
  }
  // Sociedades vía API v2 (id público `soc_`, empresa por `cmp_`, detrás de
  // webAuthV2). Las credenciales SAP nunca vuelven: sólo el flag `sapConfigured`.
  'society-v2-detail': {
    society: SocietyV2AdminRow
  }
  'society-v2-create': {
    onDone?: () => void
  }
  // Representantes legales (CRUD; regla 1:1 por sociedad).
  'representative-detail': {
    representative: RepresentativeAdminRow
  }
  'representative-create': {
    onDone?: () => void
  }
  'representative-v2-detail': {
    representative: RepresentativeV2AdminRow
  }
  'representative-v2-create': {
    onDone?: () => void
  }
  // Empresas (CRUD; type de sistema, única default, guards de borrado).
  'company-detail': {
    company: CompanyAdminRow
  }
  'company-create': {
    onDone?: () => void
  }
  // Empresas vía API v2 (id público `cmp_`, detrás de webAuthV2).
  'company-v2-detail': {
    company: CompanyV2AdminRow
  }
  'company-v2-create': {
    onDone?: () => void
  }
  // Agencias (CRUD; código único por empresa, una default por empresa).
  'agency-detail': {
    agency: AgencyAdminRow
  }
  'agency-create': {
    onDone?: () => void
  }
  // Agencias vía API v2 (id público `agc_`, empresa por `cmp_`, detrás de webAuthV2).
  'agency-v2-detail': {
    agency: AgencyV2AdminRow
  }
  'agency-v2-create': {
    onDone?: () => void
  }
  // Estados del flujo (detalle; el alta/edición vive en página completa por
  // el editor de requisitos documentales).
  'state-detail': {
    state: StateAdminRow
  }
  // Estados vía API v2 (id público `sta_`, detrás de webAuthV2). Los requisitos
  // documentales ya no viajan en la fila: el panel los pide a su subrecurso.
  'state-v2-detail': {
    state: StateV2AdminRow
  }
  // Flujos de trabajo (CRUD; nombre único global, un default por acción).
  'workflow-detail': {
    workflow: WorkflowAdminRow
  }
  'workflow-create': {
    onDone?: () => void
  }
  // Flujos de trabajo vía API v2 (id público `wfl_`, detrás de webAuthV2).
  'workflow-v2-detail': {
    workflow: WorkflowV2AdminRow
  }
  'workflow-v2-create': {
    onDone?: () => void
  }
  // Roles (detalle; el alta/edición vive en página completa por la matriz).
  'role-detail': {
    role: RoleAdminRow
  }
  // Roles vía API v2 (id público `rol_`, detrás de webAuthV2).
  'role-v2-detail': {
    role: RoleV2AdminRow
  }
}

export type SidePanelParams<TPage extends SidePanelPage = SidePanelPage> =
  SidePanelPageParams[TPage]

export interface SidePanelNavigationEntry<TPage extends SidePanelPage = SidePanelPage> {
  page: TPage
  params: SidePanelPageParams[TPage]
  pageId: string
}

export interface OpenSidePanelOptions<TPage extends SidePanelPage = SidePanelPage> {
  page: TPage
  params: SidePanelPageParams[TPage]
  resetStack?: boolean
}

export interface SidePanelPageDefinition<TPage extends SidePanelPage = SidePanelPage> {
  title: (params: SidePanelPageParams[TPage]) => string
  icon?: ReactNode
  render: (params: SidePanelPageParams[TPage]) => ReactNode
  footer?: (params: SidePanelPageParams[TPage]) => ReactNode
}
